// ABOUTME: Maps a scheduled Skylegs flight onto the StandardizedFlight TechLog consumes as a draft trip and sector.
import { StandardizedUserRole } from '../../../../types';
import { SkylegsToTechlogFlightInput } from './validation';

export const skylegsFlightToTechlogSchema = {
  originId: (input: SkylegsToTechlogFlightInput) =>
    String(input.skylegs.flight.id),

  // One trip per flight, so the flight is also the origin identity of the trip.
  // Skylegs `mission_id` deliberately is not used: two legs of one mission would
  // otherwise claim the same trip.
  originTripId: (input: SkylegsToTechlogFlightInput) =>
    String(input.skylegs.flight.id),

  // A Skylegs flight has no standalone date field; the schedule date is the
  // planned off-block time. Skylegs serializes in the tenant's timezone, so the
  // instant is normalized to UTC first to match the OOOI times below.
  date: (input: SkylegsToTechlogFlightInput) =>
    new Date(input.skylegs.flight.obt).toISOString().slice(0, 10),

  aircraft: (input: SkylegsToTechlogFlightInput) =>
    input.skylegs.flight.aircraft.registration,

  departureAirportICAO: (input: SkylegsToTechlogFlightInput) =>
    input.skylegs.flight.departure.icao,

  arrivalAirportICAO: (input: SkylegsToTechlogFlightInput) =>
    input.skylegs.flight.arrival.icao,

  offblocksTime: (input: SkylegsToTechlogFlightInput) =>
    new Date(input.skylegs.flight.obt).toISOString(),

  takeoffTime: (input: SkylegsToTechlogFlightInput) =>
    new Date(input.skylegs.flight.tot).toISOString(),

  landingTime: (input: SkylegsToTechlogFlightInput) =>
    new Date(input.skylegs.flight.ldt).toISOString(),

  onblocksTime: (input: SkylegsToTechlogFlightInput) =>
    new Date(input.skylegs.flight.ibt).toISOString(),

  // Skylegs `number` is the callsign. `flight_number` is the leg sequence within
  // a mission and is not a flight number, so it is not mapped.
  number: (input: SkylegsToTechlogFlightInput) =>
    input.skylegs.flight.number ?? undefined,

  // Skylegs categories are names the tenant defines for itself; TechLog
  // `tripCategory` is a fixed enum (ZTechlogTripCategory), so the name is
  // matched case-insensitively onto it rather than passed through.
  //
  // Only genuine correspondences are mapped. A tenant category with no TechLog
  // equivalent leaves the field unset, which TechLog accepts and the trip still
  // imports with. Choosing a nearest-looking value instead would write an
  // operational classification the operator never made onto an aviation record
  // that AMOS `service_type` and regulatory reporting both read as fact.
  // "Commercial demo" is deliberately not mapped to `commercial`: a tenant that
  // wanted those legs recorded as commercial would file them under its own
  // "Commercial" category, and a demonstration flight carries no revenue load.
  tripCategory: (input: SkylegsToTechlogFlightInput) => {
    const category = input.skylegs.flight.category?.name;
    if (!category) {
      return undefined;
    }

    const techlogCategory = {
      private: 'private',
      commercial: 'commercial',
      training: 'training',
      maintenance: 'maintenance',
    }[category.trim().toLowerCase()];

    if (!techlogCategory) {
      console.warn(
        `TechLog trip category left unset on Skylegs flight ${input.skylegs.flight.id}: Skylegs category "${category}" has no TechLog equivalent.`,
      );
    }

    return techlogCategory;
  },

  pilotInCommand: (input: SkylegsToTechlogFlightInput) => {
    // Skylegs marks the commander explicitly; the left seat is the documented
    // fallback. Agenda array order carries no contract and is not relied on.
    const agenda =
      input.skylegs.crew.find((member) => member.pilot_in_command) ??
      input.skylegs.crew.find((member) => member.position === 'left');
    if (!agenda) {
      return undefined;
    }

    // The email TechLog matches users on lives on the organisation membership,
    // and the list contains memberships that are no longer active.
    const user = input.skylegs.organisationUsers.find(
      (candidate) =>
        candidate.id === agenda.user_id && candidate.pivot.status === 'active',
    );
    if (!user?.pivot.email) {
      return undefined;
    }

    return {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.pivot.email,
      role: StandardizedUserRole.CMD,
    };
  },

  secondInCommand: (input: SkylegsToTechlogFlightInput) => {
    const commander =
      input.skylegs.crew.find((member) => member.pilot_in_command) ??
      input.skylegs.crew.find((member) => member.position === 'left');
    const agenda = input.skylegs.crew.find(
      (member) => member.id !== commander?.id && member.position === 'right',
    );
    if (!agenda) {
      return undefined;
    }

    const user = input.skylegs.organisationUsers.find(
      (candidate) =>
        candidate.id === agenda.user_id && candidate.pivot.status === 'active',
    );
    if (!user?.pivot.email) {
      return undefined;
    }

    return {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.pivot.email,
      role: StandardizedUserRole.FO,
    };
  },

  additionalCrew: (input: SkylegsToTechlogFlightInput) => {
    const commander =
      input.skylegs.crew.find((member) => member.pilot_in_command) ??
      input.skylegs.crew.find((member) => member.position === 'left');
    const secondInCommand = input.skylegs.crew.find(
      (member) => member.id !== commander?.id && member.position === 'right',
    );

    return input.skylegs.crew
      .filter(
        (member) =>
          member.id !== commander?.id && member.id !== secondInCommand?.id,
      )
      .flatMap((member) => {
        const user = input.skylegs.organisationUsers.find(
          (candidate) =>
            candidate.id === member.user_id &&
            candidate.pivot.status === 'active',
        );
        if (!user?.pivot.email) {
          return [];
        }

        // Skylegs `kind` is free text per assignment ("Commander EHS", "Captain"),
        // so no specific TechLog crew role can be derived from it.
        return [
          {
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.pivot.email,
            role: StandardizedUserRole.OTHER,
          },
        ];
      });
  },
};
